import { ChatMessage } from "../../../../views/crm-pages/conversation/types";

// Template messages in the chat — same rules as the mobile app
// (lib/messageTemplateDisplay.ts there), so web and mobile show the same text:
//  1. message_metadata.template_body (the body saved at send time) wins.
//  2. An already-rendered body (no `template:` prefix, no {{placeholders}})
//     is kept as-is — history is never swapped for today's catalog wording.
//  3. Otherwise the catalog template (by name, matching language preferred)
//     with {{tokens}} filled from metadata.template.components[].parameters[].
//  4. A bare `template:<name>` is never shown — it becomes "Template message".

export interface CatalogTemplate {
  name: string;
  language?: string;
  content: string; // with {{1}} / {{name}} placeholders
  buttons?: any;
}

const TEMPLATE_PREFIX = /^template\s*:\s*(.+)$/i;
const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
const HAS_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;
const FALLBACK_TEXT = "Template message";

const meta = (msg: ChatMessage): any => msg.message_metadata || {};

const getTemplateNameFromBody = (body?: string) => body?.trim().match(TEMPLATE_PREFIX)?.[1]?.trim() || "";

const getTemplateName = (msg: ChatMessage): string =>
  meta(msg).template?.name || meta(msg).template_name || getTemplateNameFromBody(msg.body);

const getTemplateLanguage = (msg: ChatMessage): string | undefined => {
  const lang = meta(msg).template?.language;
  return typeof lang === "string" ? lang : lang?.code;
};

export const isTemplateMessage = (msg: ChatMessage): boolean =>
  msg.type === "template" || !!getTemplateNameFromBody(msg.body) || !!meta(msg).template?.name;

const findCatalogMatches = (msg: ChatMessage, templates: CatalogTemplate[]): CatalogTemplate[] => {
  const name = getTemplateName(msg);
  if (!name) return [];
  const byName = templates.filter((t) => t.name === name);
  const lang = getTemplateLanguage(msg);
  const byLang = lang ? byName.filter((t) => t.language === lang) : [];
  return byLang.length ? byLang : byName;
};

// Named parameters bind by parameter_name, positional ones by 1-based index.
// Also reads template_attributes.body — what this web app's own send-template
// call stores — when the provider-style components aren't there.
const getParameterValues = (msg: ChatMessage) => {
  const named: Record<string, string> = {};
  const positional: string[] = [];
  const components = meta(msg).template?.components;
  if (Array.isArray(components)) {
    components
      .filter((c: any) => !c?.type || String(c.type).toLowerCase() === "body")
      .forEach((c: any) =>
        (c?.parameters || []).forEach((p: any) => {
          const value = String(p?.text ?? p?.value ?? "");
          if (p?.parameter_name) named[p.parameter_name] = value;
          else positional.push(value);
        })
      );
  }
  const attrs = meta(msg).template_attributes?.body;
  if (!positional.length && Array.isArray(attrs)) attrs.forEach((v: any) => positional.push(String(v)));
  return { named, positional };
};

const fillPlaceholders = (content: string, msg: ChatMessage) => {
  const { named, positional } = getParameterValues(msg);
  return content.replace(PLACEHOLDER, (match, key: string) => {
    if (named[key] != null) return named[key];
    const i = Number(key);
    return Number.isInteger(i) && positional[i - 1] != null ? positional[i - 1] : match;
  });
};

export const resolveTemplateText = (msg: ChatMessage, templates: CatalogTemplate[]): string => {
  const sentBody = meta(msg).template_body;
  if (typeof sentBody === "string" && sentBody.trim()) return fillPlaceholders(sentBody, msg);

  const raw = (msg.body || "").trim();
  const isWireName = !!getTemplateNameFromBody(raw);
  if (raw && !isWireName && !HAS_PLACEHOLDER.test(raw)) return raw;

  const match = findCatalogMatches(msg, templates)[0];
  const content = match?.content ? fillPlaceholders(match.content, msg) : "";
  return content || (!isWireName && raw ? fillPlaceholders(raw, msg) : "") || FALLBACK_TEXT;
};

const optionLabel = (o: any): string => (typeof o === "string" ? o : o?.text || o?.title || o?.label || "");

const catalogButtonLabels = (buttons: any): string[] => {
  if (Array.isArray(buttons)) return buttons.map(optionLabel).filter(Boolean);
  if (Array.isArray(buttons?.buttons)) return buttons.buttons.map(optionLabel).filter(Boolean);
  return [];
};

// The sent snapshot (metadata.template_options) is authoritative — an
// explicit [] means none, with no catalog fallback, so changed button labels
// are never invented. Legacy messages without it fall back to the catalog
// only on exactly one name/language match.
export const resolveTemplateOptions = (
  msg: ChatMessage,
  templates: CatalogTemplate[]
): { options: string[]; source: "sent" | "catalogue" } | null => {
  const sent = meta(msg).template_options;
  if (Array.isArray(sent)) {
    const options = sent.map(optionLabel).filter(Boolean);
    return options.length ? { options, source: "sent" } : null;
  }
  if (!meta(msg).template?.name && !getTemplateNameFromBody(msg.body)) return null;
  const candidates = findCatalogMatches(msg, templates);
  if (candidates.length !== 1) return null;
  const options = catalogButtonLabels(candidates[0].buttons);
  return options.length ? { options, source: "catalogue" } : null;
};
